import React, { useState } from "react";
import { Formik, Field, Form, ErrorMessage } from "formik";
import * as Yup from "yup";
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Alert from "react-bootstrap/Alert";
import Button from "./Button";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUser, faEnvelope, faMapMarkerAlt, faComment, faPaperPlane, faPhone, faLocationDot } from "@fortawesome/free-solid-svg-icons";

const Contact = () => {
  const initialValues = {
    name: "",
    email: "",
    country: "",
    reason: "",
  };

  const validationSchema = Yup.object().shape({
    name: Yup.string().required("Your name is required"),
    email: Yup.string()
      .email("Invalid email")
      .required("Your email is required"),
    reason: Yup.string().required("Your message is required"),
  });

  const [showAlert, setShowAlert] = useState(false);

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    try {
      const response = await fetch("https://formspree.io/f/xbjvoprp", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(values),
      });

      if (response.ok) {
        setShowAlert(true);
        setSubmitting(false);
        resetForm();
      } else {
        console.error("Failed to submit form");
      }
    } catch (error) {
      console.error("An error occurred while submitting the form:", error);
    }
  };

  return (
    <section className="contact-section">
      <Container>
        <div className="section-header">
          <span className="section-label">Get In Touch</span>
          <h2 className="section-title">Contact Us</h2>
          <p className="section-subtitle">
            Have a project in mind? We'd love to hear from you. Send us a message and we'll respond as soon as possible.
          </p>
        </div>

        <Row className="contact-content">
          <Col lg={5} className="contact-info-col">
            <div className="contact-info-card">
              <h3 className="contact-info-title">Let's Talk</h3>
              <p className="contact-info-text">
                Ready to transform your products? Our team is here to help you find the perfect finishing solution.
              </p>

              <div className="contact-info-items">
                <div className="contact-info-item">
                  <div className="contact-info-icon">
                    <FontAwesomeIcon icon={faPhone} />
                  </div>
                  <div>
                    <span className="contact-info-label">Phone</span>
                    <a href="tel:+96103518323" className="contact-info-value">+961 03 518 323</a>
                  </div>
                </div>

                <div className="contact-info-item">
                  <div className="contact-info-icon">
                    <FontAwesomeIcon icon={faEnvelope} />
                  </div>
                  <div>
                    <span className="contact-info-label">Email</span>
                    <a href="mailto:ralphkamel.rk@gmail.com" className="contact-info-value">ralphkamel.rk@gmail.com</a>
                  </div>
                </div>

                <div className="contact-info-item">
                  <div className="contact-info-icon">
                    <FontAwesomeIcon icon={faLocationDot} />
                  </div>
                  <div>
                    <span className="contact-info-label">Address</span>
                    <span className="contact-info-value">54 Industrial Drive, Zouk, Lebanon</span>
                  </div>
                </div>
              </div>
            </div>
          </Col>

          <Col lg={7} className="contact-form-col">
            <div className="contact-form-card">
              <Formik
                initialValues={initialValues}
                validationSchema={validationSchema}
                onSubmit={handleSubmit}
              >
                {({ isSubmitting }) => (
                  <Form className="contact-form">
                    <Row>
                      <Col md={6}>
                        <div className="form-group">
                          <label htmlFor="name" className="form-label-enhanced">
                            <FontAwesomeIcon icon={faUser} className="form-icon" />
                            Name/Company <span className="required">*</span>
                          </label>
                          <Field
                            type="text"
                            id="name"
                            name="name"
                            placeholder="Your name or company"
                            className="form-input"
                          />
                          <ErrorMessage
                            name="name"
                            component="div"
                            className="form-error"
                          />
                        </div>
                      </Col>
                      <Col md={6}>
                        <div className="form-group">
                          <label htmlFor="email" className="form-label-enhanced">
                            <FontAwesomeIcon icon={faEnvelope} className="form-icon" />
                            Email <span className="required">*</span>
                          </label>
                          <Field
                            type="email"
                            id="email"
                            name="email"
                            placeholder="your@email.com"
                            className="form-input"
                          />
                          <ErrorMessage
                            name="email"
                            component="div"
                            className="form-error"
                          />
                        </div>
                      </Col>
                    </Row>

                    <div className="form-group">
                      <label htmlFor="country" className="form-label-enhanced">
                        <FontAwesomeIcon icon={faMapMarkerAlt} className="form-icon" />
                        Location
                      </label>
                      <Field
                        type="text"
                        id="country"
                        name="country"
                        placeholder="Your location"
                        className="form-input"
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="reason" className="form-label-enhanced">
                        <FontAwesomeIcon icon={faComment} className="form-icon" />
                        Message <span className="required">*</span>
                      </label>
                      <Field
                        as="textarea"
                        id="reason"
                        name="reason"
                        rows="5"
                        placeholder="Tell us about your project..."
                        className="form-input form-textarea"
                      />
                      <ErrorMessage
                        name="reason"
                        component="div"
                        className="form-error"
                      />
                    </div>

                    <Button
                      type="submit"
                      variant="primary"
                      size="lg"
                      fullWidth
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? (
                        "Sending..."
                      ) : (
                        <>
                          Send Message <FontAwesomeIcon icon={faPaperPlane} className="ms-2" />
                        </>
                      )}
                    </Button>
                  </Form>
                )}
              </Formik>

              {showAlert && (
                <Alert
                  variant="success"
                  onClose={() => setShowAlert(false)}
                  dismissible
                  className="mt-4 success-alert"
                >
                  Thank you for your submission! We will reply to you shortly.
                </Alert>
              )}
            </div>
          </Col>
        </Row>
      </Container>
    </section>
  );
};

export default Contact;
